import { useClient } from '@cortex/react-query';
import type { Judge } from '@cortex/types';
import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';

import { EmptyState } from '../../components/common/EmptyState';
import { PageHeader } from '../../components/common/PageHeader';
import { Spinner } from '../../components/common/Spinner';
import { Table, type IColumn } from '../../components/common/Table';
import { routes } from '../../lib/constants';
import { formatDateTime } from '../../lib/format';

export const Route = createFileRoute('/judges/')({ component: JudgesPage });

interface IListEnvelope<T> {
  items: T[];
  total: number;
}

function JudgesPage() {
  const client = useClient();
  const nav = useNavigate();

  const list = useQuery({
    queryKey: ['judges'],
    queryFn: () => client.http.get('v1/judges').json<IListEnvelope<Judge>>(),
  });

  const cols: IColumn<Judge>[] = [
    {
      key: 'name',
      header: 'Name',
      cell: (j) => (
        <Link
          to={routes.judgeDetail(j.id)}
          style={{ color: 'var(--text-primary)', fontWeight: 'var(--fw-med)' }}
        >
          {j.name}
        </Link>
      ),
      width: 240,
    },
    {
      key: 'type',
      header: 'Type',
      cell: (j) => (
        <span className="fk-badge">{j.judgeType === 'CODE' ? 'Code' : 'LLM one-shot'}</span>
      ),
      width: 140,
    },
    {
      key: 'description',
      header: 'Description',
      cell: (j) => j.description ?? '—',
    },
    {
      key: 'updated',
      header: 'Updated',
      mono: true,
      cell: (j) => formatDateTime(j.updatedAt),
      width: 200,
    },
  ];

  const onRowClick = async (j: Judge) => {
    try {
      await nav({ to: routes.judgeDetail(j.id) });
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <>
      <PageHeader
        title="Judges"
        actions={
          <Link to={routes.JUDGES_NEW} className="btn btn--primary btn--sm">
            New judge
          </Link>
        }
      />
      {list.isLoading ? (
        <Spinner />
      ) : list.error ? (
        <div className="alert alert--danger">Failed to load: {(list.error as Error).message}</div>
      ) : (list.data?.items ?? []).length === 0 ? (
        <EmptyState
          title="No judges yet"
          description="Create a judge to score workflow outputs deterministically (Code) or via an LLM (one-shot)."
        />
      ) : (
        <Table<Judge>
          rows={list.data?.items ?? []}
          columns={cols}
          rowKey={(j) => j.id}
          onRowClick={onRowClick}
        />
      )}
    </>
  );
}
